import NProgress from 'nprogress';
import { Switch, Route, Redirect } from 'react-router-dom';
import { Suspense, Fragment, lazy, useEffect, useMemo } from 'react';
// material
import { makeStyles } from '@material-ui/core/styles';
// guards
import GuestGuard from '../guards/GuestGuard';
// components
import LoadingScreen from '../components/LoadingScreen';
//
import AuthGuard from '../guards/AuthGuard';
// layouts
import DashboardLayout from '../layouts/dashboard';
import HomeLayout from '../layouts/home';
import {
  PATH_PAGE,
  PATH_AUTH,
  PATH_DASHBOARD,
  mode,
  folderName,
} from './paths';

// ----------------------------------------------------------------------

const nprogressStyle = makeStyles((theme) => ({
  '@global': {
    '#nprogress': {
      pointerEvents: 'none',
      '& .bar': {
        top: 0,
        left: 0,
        height: 2,
        width: '100%',
        position: 'fixed',
        zIndex: theme.zIndex.snackbar,
        backgroundColor: theme.palette.primary.main,
        boxShadow: `0 0 2px ${theme.palette.primary.main}`,
      },
      '& .peg': {
        right: 0,
        opacity: 1,
        width: 100,
        height: '100%',
        display: 'block',
        position: 'absolute',
        transform: 'rotate(3deg) translate(0px, -4px)',
        boxShadow: `0 0 10px ${theme.palette.primary.main}, 0 0 5px ${theme.palette.primary.main}`,
      },
    },
  },
}));

function RouteProgress(props) {
  nprogressStyle();

  NProgress.configure({
    speed: 500,
    showSpinner: false,
  });

  useMemo(() => {
    NProgress.start();
  }, []);

  useEffect(() => {
    NProgress.done();
  }, []);

  return <Route {...props} />;
}

export function renderRoutes(routes = []) {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        {routes.map((route, idx) => {
          const Component = route.component;
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;

          return (
            <RouteProgress
              key={`routes-${idx}`}
              path={route.path}
              exact={route.exact}
              render={(props) => (
                <Guard>
                  <Layout>
                    {route.routes ? (
                      renderRoutes(route.routes)
                    ) : (
                      <Component {...props} />
                    )}
                  </Layout>
                </Guard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  );
}

const routes = [
  // Others Routes
  {
    exact: true,
    guard: GuestGuard,
    path: PATH_AUTH.login,
    component: lazy(() => import('../views/authentication/Login')),
  },
  {
    exact: true,
    path: PATH_AUTH.loginUnprotected,
    component: lazy(() => import('../views/authentication/Login')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: PATH_AUTH.register,
    component: lazy(() => import('../views/authentication/Register')),
  },
  {
    exact: true,
    path: PATH_AUTH.registerUnprotected,
    component: lazy(() => import('../views/authentication/Register')),
  },
  {
    exact: true,
    path: PATH_AUTH.resetPassword,
    component: lazy(() => import('../views/authentication/ResetPassword')),
  },
  {
    exact: true,
    path: PATH_AUTH.verify,
    component: lazy(() => import('../views/authentication/VerifyCode')),
  },
  {
    exact: true,
    path: PATH_PAGE.page404,
    component: lazy(() => import('../views/general/Page404')),
  },
  {
    exact: true,
    path: PATH_PAGE.page500,
    component: lazy(() => import('../views/general/Page500')),
  },
  {
    exact: true,
    path: PATH_PAGE.maintenance,
    component: lazy(() => import('../views/general/Maintenance')),
  },
  {
    exact: true,
    path: PATH_AUTH.root,
    component: () => <Redirect to={PATH_AUTH.login} />,
  },
  {
    path: PATH_DASHBOARD.root,
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: PATH_DASHBOARD.root,
        component: () => <Redirect to={PATH_DASHBOARD.general.app} />,
      },
      //FEATURES TK NEW
      {
        exact: true,
        path: PATH_DASHBOARD.intrakurikuler.aspect_indicator,
        component: lazy(() =>
          import('../views/aspect_indicators/AspectIndicator'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.term,
        component: lazy(() => import('../views/terms/List')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.assesment_category,
        component: lazy(() => import('../views/assesment_categories/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.chinese_name_setting,
        component: lazy(() => import('../views/chinese_name_settings/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.assesment.list,
        component: lazy(() => import('../views/assesment_terms/List')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.assesment.form,
        component: lazy(() => import('../views/assesment_terms/Form')),
      },
      //FEATURES TK NEW
      {
        exact: true,
        path: PATH_DASHBOARD.general.app,
        component: lazy(() => import('../views/dashboard/DashboardContent')),
      },

      {
        exact: true,
        path: PATH_DASHBOARD.profile,
        component: lazy(() => import('../views/profile/Profile')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.prosem,
        component: lazy(() => import('../views/prosem/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.prota,
        component: lazy(() => import('../views/prota/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.silabus,
        component: lazy(() => import('../views/silabus/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rpp,
        component: lazy(() => import('../views/rpp/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.sumatif,
        component: lazy(() => import('../views/sumatif/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rbm,
        component: lazy(() => import('../views/rbm/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.mapel,
        component: lazy(() => import('../views/daftarMapel/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.penjadwalan,
        component: lazy(() => import('../views/penjadwalan/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.beranda,
        component: lazy(() => import('../views/beranda/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.beranda,
        component: lazy(() => import('../views/beranda/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.bahasa,
        component: lazy(() => import('../views/bahasa/Bahasa')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.matapelajaran,
        component: lazy(() => import('../views/mapelsk')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.matapelajaranekskur,
        component: lazy(() => import('../views/mapelekskur/MapelEksKur')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.kelas,
        component: lazy(() => import('../views/kelas/Kelas')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.paralel,
        component: lazy(() => import('../views/paralel/Paralel')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.jurusan,
        component: lazy(() => import('../views/jurusan/Jurusan')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.matapelajaranpilihan,
        component: lazy(() => import('../views/mapil/Mapil')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.matapelajarangabungan,
        component: lazy(() => import('../views/mapelGabungan')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.ekskul,
        component: lazy(() => import('../views/ekskul/listEkskul')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.ekskulKelas,
        component: lazy(() => import('../views/ekskul/ekskulKelas')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.ekskul_category,
        component: lazy(() => import('../views/ekskul/ekskulCategory')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.ekskul_student,
        component: lazy(() => import('../views/ekskul/ekskulStudent')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.ekskul_note,
        component: lazy(() => import('../views/ekskul/ekskulNote')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.ekskul_presence,
        component: lazy(() => import('../views/ekskul/ekskul-presence')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.kikdSatuDua,
        component: lazy(() => import('../views/kikdSatuDua/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.kikdTigaEmpat,
        component: lazy(() => import('../views/kikd/Index')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.alokasiwaktu,
        component: lazy(() => import('../views/alokasiwaktu/AlokasiWaktu')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.laporan_alokasi_waktu,
        component: lazy(() =>
          import('../views/laporan_alokasi_waktu/LaporanAlokasiWaktu'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.alokasi_waktu.formDetail,
        component: lazy(() =>
          import('../views/alokasiwaktu/AlokasiWaktuDetail'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.pembagiantugas,
        component: lazy(() => import('../views/pembagiantugas/PembagianTugas')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.pembagiantugasnew,
        component: lazy(() => import('../views/pembagiantugasNew')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.pembagian_tugas.formDetail,
        component: lazy(() =>
          import('../views/pembagiantugas/PembagianTugasDetail'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.pembagian_tugas.bykelas,
        component: lazy(() =>
          import('../views/pembagiantugas/PembagianTugasByKelas'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.laporan_pembagian_tugas,
        component: lazy(() =>
          import('../views/laporan_pembagian_tugas/Laporan_Pembagian_Tugas'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.calendar,
        component: lazy(() => import('../views/calendar/Calendar')),
      },
      {
        //create by Eko 30 Jan 2024
        exact: true,
        path: PATH_DASHBOARD.laporan_kegiatan,
        component: lazy(() =>
          import('../views/laporan_kegiatan/LaporanKegiatan'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.pengaturan_siswa,
        component: lazy(() =>
          import('../views/pengaturan_siswa/PengaturanSiswa'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.pengaturan_siswa_edit,
        component: lazy(() => import('../views/pengaturan_siswa/EditSiswa')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.pengaturan_buku_induk,
        component: lazy(() =>
          import('../views/pengaturanBukuInduk/PengaturanBukuInduk'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.pengaturan_buku_panduan,
        component: lazy(() => import('../views/buku_panduan/BukuPanduan')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.edit_buku_induk,
        component: lazy(() =>
          import('../views/pengaturanBukuInduk/EditBukuInduk'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.jampelajaran,
        component: lazy(() => import('../views/jampelajaran/JamPelajaran')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.alokasiWalikelas,
        component: lazy(() =>
          import('../views/alokasiWalikelas/AlokasiWalikelas'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.jadwalguru,
        component: lazy(() => import('../views/jadwalguru/JadwalGuru')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.absensiSiswa,
        component: lazy(() => import('../views/absensiSiswa/AbsensiSiswa')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.jadwal_guru.formDetail,
        component: lazy(() => import('../views/jadwalguru/JadwalGuruDetail')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.jadwalpelajaran,
        component: lazy(() =>
          import('../views/laporan_jadwal_pelajaran/LaporanJadwalPelajaran'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.jadwalPelajaranUpload,
        component: lazy(() => import('../views/jadwal-pelajaran-upload')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.settingJadwalInput,
        component: lazy(() => import('../views/settings/jadwalInput')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.settingJadwalInputKategori,
        component: lazy(() => import('../views/settings/jadwalInputKategori')),
      },
      // ----------------------------------------------------------------------
      {
        exact: true,
        path: PATH_DASHBOARD.dimensi,
        component: lazy(() => import('../views/dimensi/Dimensi')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.alokasiGuruP5,
        component: lazy(() => import('../views/alokasi-gurup5/AlokasiGuruP5')),
      },

      {
        exact: true,
        path: PATH_DASHBOARD.agendaGuru,
        component: lazy(() => import('../views/agendaGuru/AgendaGuru')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.jurnalKelas,
        component: lazy(() => import('../views/JurnalKelas/JurnalKelas')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.penilaian_proyek,
        component: lazy(() =>
          import('../views/penilaian_proyek/PenilaianProyek'),
        ),
      },
      // {
      //   exact: true,
      //   path: PATH_DASHBOARD.penilaian_proyek_detail.formDetail,
      //   component: lazy(() =>
      //     import("../views/penilaian_proyek/PenilaianProyekDetail")
      //   ),

      // },
      {
        exact: true,
        path: PATH_DASHBOARD.catatan_proses,
        component: lazy(() => import('../views/catatan_proses/CatatanProses')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.template_catatan_proses,
        component: lazy(() =>
          import('../views/template_catatan_proses/TemplateCatatanProses'),
        ),
      },

      {
        exact: true,
        path: PATH_DASHBOARD.proyek,
        component: lazy(() => import('../views/proyek/Proyek')),
      },

      {
        exact: true,
        path: PATH_DASHBOARD.proyek_detail.formDetail,
        component: lazy(() => import('../views/proyek/ProyekDetail')),
      },

      //-------
      {
        exact: true,
        path: PATH_DASHBOARD.fase,
        component: lazy(() => import('../views/fase')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.intrakurikuler.ruang_lingkup_materi,
        component: lazy(() =>
          import('../views/intrakurikuler/sumatif/ruangLingkupMateri'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.intrakurikuler.tujuan_pembelajaran,
        component: lazy(() =>
          import('../views/intrakurikuler/formatif/tujuanPembelajaran'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.intrakurikuler.master,
        component: lazy(() =>
          import('../views/intrakurikuler/master-intrakurikuler'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.intrakurikuler.upload_materi,
        component: lazy(() => import('../views/intrakurikuler/upload_materi')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.intrakurikuler.upload_tugas,
        component: lazy(() => import('../views/intrakurikuler/upload_tugas')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.intrakurikuler.student_excercise,
        component: lazy(() =>
          import('../views/intrakurikuler/student-excercise'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.intrakurikuler.upload_materi_tk,
        component: lazy(() =>
          import('../views/intrakurikuler/upload_materi_tk'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.penilaian.formatif,
        component: lazy(() =>
          import('../views/penilaianFormatifSumatif/formatif'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.penilaian.sumatif,
        component: lazy(() =>
          import('../views/penilaianFormatifSumatif/sumatif'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.penilaian.sumatif_akhir,
        component: lazy(() =>
          import('../views/penilaianFormatifSumatif/sumatif_akhir'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.penilaian.nus,
        component: lazy(() => import('../views/penilaianFormatifSumatif/nus')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.penilaian.ekskul,
        component: lazy(() =>
          import('../views/penilaianFormatifSumatif/ekskul'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.penilaian.akhir,
        component: lazy(() =>
          import('../views/penilaianFormatifSumatif/akhir'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.laporan.formatif,
        component: lazy(() => import('../views/laporan/formatif')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.laporan.sumatif,
        component: lazy(() => import('../views/laporan/sumatif')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.laporan.ekskul,
        component: lazy(() => import('../views/laporan/ekskul')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.laporan.sumatif_as,
        component: lazy(() => import('../views/laporan/sumatif_akhir')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.report.weekly_for_teacher,
        component: lazy(() => import('../views/reports/teachers/Weekly')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.report.weekly_for_parent,
        component: lazy(() => import('../views/reports/parents/Weekly')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.general_ledger,
        component: lazy(() => import('../views/rapor/general_ledger')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.general_ledger_real,
        component: lazy(() => import('../views/rapor/general_ledger_real')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.generate_sumatif_formatif,
        component: lazy(() =>
          import('../views/rapor/generate_sumatif_formatif'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.mid_semester,
        component: lazy(() => import('../views/rapor/generate_mid_semester')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.sumatif_formatif,
        component: lazy(() => import('../views/rapor/sumatif_formatif')),
      },
      {
        // tambahan by eko untuk cetak rapor P5
        exact: true,
        path: PATH_DASHBOARD.rapor.rapor_proyek,
        component: lazy(() => import('../views/rapor_proyek/RaporProyek')),
      },
      {
        // tambahan by anwar untuk cetak rapor TK
        exact: true,
        path: PATH_DASHBOARD.rapor.rapor_tk,
        component: lazy(() => import('../views/rapor/tk')),
      },
      {
        // tambahan by anwar untuk cetak rapor TK
        exact: true,
        path: PATH_DASHBOARD.rapor.rapor_tk_wakil,
        component: lazy(() => import('../views/rapor/tk/index_wakil')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.setting,
        component: lazy(() => import('../views/rapor/setting')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.profile_sekolah,
        component: lazy(() => import('../views/profileSekolah')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.kepala_sekolah,
        component: lazy(() => import('../views/kepala_sekolah')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.tahun_pelajaran,
        component: lazy(() => import('../views/tahun_pelajaran')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.mapelpilihanwajib.mapelpilihanwajib,
        component: lazy(() =>
          import('../views/mapelpilihanwajib/MapelPilihanWajib'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.mapelpilihanwajibapproval,
        component: lazy(() =>
          import('../views/mapelpilihanwajibapproval/ApprovalList'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.cover.coverraport,
        component: lazy(() => import('../views/rapor/cover')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.urutan_rapor,
        component: lazy(() => import('../views/rapor/urutan_rapor')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.mapil_siswa,
        component: lazy(() => import('../views/mapilSiswa')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.mapil_kelas,
        component: lazy(() => import('../views/mapil-kelas')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.mapel_siswa,
        component: lazy(() => import('../views/mapelSiswa')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.catatan_walikelas,
        component: lazy(() => import('../views/catatan_walikelas')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.catatan_walikelas_tk,
        component: lazy(() => import('../views/catatan_walikelas_tk')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.prestasi_siswa,
        component: lazy(() => import('../views/prestasi_siswa')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.setting_tanggal,
        component: lazy(() => import('../views/rapor/setting_tanggal_rapor')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.setting_penilaian_rapor,
        component: lazy(() => import('../views/rapor/setting_penilaian_rapor')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.setting_persentase_kelulusan,
        component: lazy(() =>
          import('../views/rapor/setting_persentase_kelulusan'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.generate_nilai_kelulusan,
        component: lazy(() =>
          import('../views/rapor/generate_nilai_kelulusan'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rapor.general_ledger_kelulusan,
        component: lazy(() =>
          import('../views/rapor/general_ledger_kelulusan'),
        ),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.tugas_tambahan,
        component: lazy(() => import('../views/tugas_tambahan')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.siswa_non_aktif,
        component: lazy(() => import('../views/siswa_non_aktif')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.siswa_mutasi_manual,
        component: lazy(() => import('../views/siswa_mutasi_manual')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.pengajar_ekskul,
        component: lazy(() => import('../views/ekskul/ekskulTeacher')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.information,
        component: lazy(() => import('../views/information')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.gallery,
        component: lazy(() => import('../views/gallery')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.notulen.rapat_rumpun,
        component: lazy(() => import('../views/notulen/rapat_rumpun')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.setting_buka_tutup,
        component: lazy(() => import('../views/setting_buka_tutup')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.students,
        component: lazy(() => import('../views/students')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.studentList,
        component: lazy(() => import('../views/student-list')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.catatan_kesiswaan,
        component: lazy(() => import('../views/catatan_kesiswaan')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.jadwal_ulangan,
        component: lazy(() => import('../views/jadwal-ulangan')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.jadwal_ulangan_pas,
        component: lazy(() => import('../views/jadwal-ulangan-pas')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.jadwal_ulangan_sumatif,
        component: lazy(() => import('../views/jadwal-ulangan-sumatif')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.rombel,
        component: lazy(() => import('../views/rombel')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.alokasi_keterampilan,
        component: lazy(() => import('../views/alokasi-keterampilan')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.alokasi_kesenian,
        component: lazy(() => import('../views/alokasi-kesenian')),
      },
      {
        exact: true,
        path: PATH_DASHBOARD.upload_nilai_rapor_5,
        component: lazy(() => import('../views/upload-nilai-rapor-5')),
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
  {
    path: '*',
    layout: HomeLayout,
    routes: [
      {
        exact: true,
        path: mode === 'LOCAL' ? '/' : `/${folderName}`,
        component: () => (
          <Redirect
            to={mode === 'LOCAL' ? '/dashboard' : `/${folderName}/dashboard`}
          />
        ),
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
];

export default routes;
